import { createPortal } from 'react-dom';
import { Trans, useTranslation } from 'react-i18next';

import { Text } from '@actual-app/components/text';
import { View } from '@actual-app/components/view';
import { css, keyframes } from '@emotion/css';

import { Boton } from '#components/mobile/ui/Boton';
import { Icono } from '#components/mobile/ui/Icono';
import {
  color,
  movimiento,
  radio,
  sombra,
  suave,
  texto,
} from '#components/mobile/ui/tokens';

/**
 * Por encima de TODO: barra de pestañas y brújula (100), hojas (205)…
 */
export const VENTANA_ACTUALIZACION_Z_INDEX = 400;

const aparece = keyframes({ from: { opacity: 0 }, to: { opacity: 1 } });
const entra = keyframes({
  from: { opacity: 0, transform: 'translateY(12px) scale(0.97)' },
  to: { opacity: 1, transform: 'translateY(0) scale(1)' },
});

// Velo con el fondo borroso; si el navegador no sabe difuminar, velo oscuro.
const claseVelo = css({
  position: 'fixed',
  inset: 0,
  zIndex: VENTANA_ACTUALIZACION_Z_INDEX,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: 24,
  backgroundColor: 'rgba(14, 20, 36, 0.62)',
  animation: `${aparece} ${movimiento.fondo}ms ease-out`,
  '@supports ((backdrop-filter: blur(1px)) or (-webkit-backdrop-filter: blur(1px)))':
    {
      backgroundColor: 'rgba(14, 20, 36, 0.28)',
      backdropFilter: 'blur(10px)',
      WebkitBackdropFilter: 'blur(10px)',
    },
});

type VentanaActualizacionProps = {
  onActualizar: () => void;
  onMasTarde: () => void;
  /** Mientras se aplica (la página va a recargar). */
  aplicando?: boolean;
};

/** Ventana centrada «Hay una versión nueva de Rumbo». */
export function VentanaActualizacion({
  onActualizar,
  onMasTarde,
  aplicando = false,
}: VentanaActualizacionProps) {
  const { t } = useTranslation();
  return createPortal(
    <div className={claseVelo} data-testid="velo-actualizacion">
      <View
        role="dialog"
        aria-modal
        aria-labelledby="titulo-actualizacion"
        data-testid="ventana-actualizacion"
        style={{
          width: '100%',
          maxWidth: 360,
          backgroundColor: color.surface,
          color: color.fg,
          borderRadius: radio.heroe,
          boxShadow: sombra.flotante,
          padding: '24px 20px 20px',
          alignItems: 'center',
          textAlign: 'center',
          gap: 12,
          animation: `${entra} ${movimiento.fondo}ms ${movimiento.muelle}`,
        }}
      >
        <View
          style={{
            width: 56,
            height: 56,
            borderRadius: radio.pildora,
            backgroundColor: suave(color.accent, 16),
            color: color.accent,
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Icono nombre="velero" size={26} />
        </View>
        <Text
          id="titulo-actualizacion"
          style={{ ...texto.titulo, fontSize: 18, color: color.fg }}
        >
          {t('There is a new version of Rumbo')}
        </Text>
        <Text style={{ ...texto.cuerpo, color: color.fg2 }}>
          {t('Update to get the latest improvements. It takes a few seconds.')}
        </Text>
        <View style={{ width: '100%', gap: 8, marginTop: 8 }}>
          <Boton bloque onPress={onActualizar} isDisabled={aplicando} autoFocus>
            {aplicando ? t('Updating…') : t('Update')}
          </Boton>
          <Boton
            bloque
            variante="fantasma"
            onPress={onMasTarde}
            isDisabled={aplicando}
          >
            <Trans>Later</Trans>
          </Boton>
        </View>
      </View>
    </div>,
    document.body,
  );
}
